// src/pages/ProductsStockAdminPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../config/api";

type Product = {
  id: number;
  code: string;
  name: string;
  price: number;
  wholesalePrice: number;
  stockOnHand: number;
  unit?: string;
  createdAt?: string;
  imageUrl?: string;  // SAS 1 ชม.
  thumbUrl?: string;
};

type Movement = {
  id: number;
  product_code: string;
  qty: number;
  qty_sell: number;
  unit_cost: number;
  reason: "PURCHASE" | "SALE" | "ADJUST";
  ref_table: string | null;
  ref_no: string;
  created_at: string;
  status: string | null;
};

type Alert = { show: boolean; message: string; type?: "success" | "warning" | "info" | "danger" };

export default function ProductsStockAdminPage(): JSX.Element {
  /** ---------- UI State ---------- */
  const [items, setItems] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [toast, setToast] = useState<Alert>({ show: false, message: "", type: "success" });

  // search
  const [q, setQ] = useState<string>("");
  const [qInput, setQInput] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(50);
  const [total, setTotal] = useState<number>(0);

  // image preview (hover)
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // ----- Movement Modal states -----
  const [showMovModal, setShowMovModal] = useState(false);
  const [movLoading, setMovLoading] = useState(false);
  const [movError, setMovError] = useState<string>("");
  const [movItems, setMovItems] = useState<Movement[]>([]);
  const [movCode, setMovCode] = useState<string>("");
  const [movLimit] = useState<number>(50);
  const [movOffset, setMovOffset] = useState<number>(0);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  /** ---------- Toast auto-hide ---------- */
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast({ show: false, message: "" }), 2200);
    return () => clearTimeout(t);
  }, [toast.show]);

  function extractCodeNumber(code: string): number | null {
    const m = String(code).match(/(\d+)(?!.*\d)/);
    return m ? Number(m[1]) : null;
  }

  /** ---------- fetch products ---------- */
  // PMN-CRITICAL(/ProductsStockAdminPage): Product stock shown here comes from products.stock_on_hand via /productsListNew; reconcile with inventory_movements after deletes.
  const fetchProducts = async (query?: string, pageParam?: number) => {
    try {
      setLoading(true);
      setErrorMsg("");

      const pageToUse = pageParam ?? page;
      const qs: string[] = [];

      if (query && query.trim()) {
        qs.push(`q=${encodeURIComponent(query.trim())}`);
      }

      qs.push(`page=${pageToUse}`);
      qs.push(`limit=${limit}`);

      const url = apiUrl(`/productsListNew${qs.length ? "?" + qs.join("&") : ""}`);

      const resp = await fetch(url);
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.error ?? resp.statusText);

      const list: Product[] = (json.items ?? []).map((r: any) => ({
        id: r.id,
        code: r.code,
        name: r.name,
        price: Number(r.price ?? 0),
        wholesalePrice: Number(r.wholesalePrice ?? 0),
        stockOnHand: Number(r.stockOnHand ?? 0),
        unit: r.unit ?? undefined,
        createdAt: r.createdAt ?? undefined,
        imageUrl: r.imageUrl ?? undefined,
        thumbUrl: r.thumbUrl ?? r.imageUrl ?? undefined,
      }));

      const sorted = [...list].sort((a, b) => {
        const na = extractCodeNumber(a.code);
        const nb = extractCodeNumber(b.code);

        if (na != null && nb != null && na !== nb) return na - nb;
        if (na != null && nb == null) return -1;
        if (na == null && nb != null) return 1;

        return a.code.localeCompare(b.code, "th-TH", { numeric: true, sensitivity: "base" });
      });

      setItems(sorted);
      setTotal(Number(json.total ?? 0));
      setPage(Number(json.page ?? pageToUse));
    } catch (err: any) {
      const msg = err?.message ?? "โหลดรายการสินค้าไม่สำเร็จ";
      setErrorMsg(msg);
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(undefined, 1);
  }, []);

  // ค้นหาแบบ server
  const doSearch = () => {
    const qVal = qInput.trim();
    setQ(qVal);
    setPage(1);
    fetchProducts(qVal || undefined, 1);
  };

  // รีเฟรช
  const refresh = () => {
    fetchProducts(q || undefined, page);
  };

  // กรองฝั่ง client
  const filtered = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
        const na = extractCodeNumber(a.code);
        const nb = extractCodeNumber(b.code);
        
        // ตัวเลขมาก → น้อย
        if (na !== null && nb !== null && na !== nb) return nb - na;
        
        // ถ้าเลขเท่ากัน หรือไม่ใช่ตัวเลข → เรียงโค้ดแบบตัวอักษรจาก Z → A
        return b.code.localeCompare(a.code, undefined, { numeric: true, sensitivity: "base" });
        });
    return copy;
  }, [items]);

  /** ---------- Movement handlers ---------- */
  // PMN-CRITICAL(/ProductsStockAdminPage): Movement modal reads real inventory_movements rows for one product; reset offset/items before each product switch.
  const openMovements = async (code: string) => {
    setMovCode(code);
    setMovItems([]);
    setMovError("");
    setMovOffset(0);
    setShowMovModal(true);
    await fetchMovements(code, 0);
  };

  const fetchMovements = async (code: string, offset: number) => {
    try {
      setMovLoading(true);
      setMovError("");

      const url = apiUrl(
        `/inventory_movements?product_code=${encodeURIComponent(code)}&limit=${movLimit}&offset=${offset}`
      );

      const resp = await fetch(url);
      const ct = resp.headers.get("content-type") || "";
      const isJson = ct.includes("application/json");
      const payload = isJson ? await resp.json() : await resp.text();

      if (!resp.ok) {
        const msg = isJson ? (payload?.error ?? resp.statusText) : `HTTP ${resp.status} — ${String(payload).slice(0, 120)}`;
        throw new Error(msg);
      }

      const list: Movement[] = (payload.items ?? []).map((r: any) => ({
        id: r.id,
        product_code: r.product_code,
        qty: Number(r.qty ?? 0),
        qty_sell: Number(r.qty_sell ?? 0),
        unit_cost: Number(r.unit_cost ?? 0),
        reason: r.reason,
        ref_table: r.ref_table ?? null,
        ref_no: r.ref_no ?? "-",
        created_at: r.created_at,
        status: r.status ?? null,
      }));
      setMovItems(list);
    } catch (err: any) {
      setMovError(err?.message ?? "โหลดประวัติไม่สำเร็จ");
    } finally {
      setMovLoading(false);
    }
  };

  const nextMovPage = () => {
    const newOffset = movOffset + movLimit;
    setMovOffset(newOffset);
    fetchMovements(movCode, newOffset);
  };
  const prevMovPage = () => {
    const newOffset = Math.max(movOffset - movLimit, 0);
    setMovOffset(newOffset);
    fetchMovements(movCode, newOffset);
  };

  // PMN-CRITICAL(/ProductsStockAdminPage): Deleting a movement removes a real stock lot; backend must block sold/keychain/accessory-linked rows.
  const deleteMovement = async (m: Movement) => {
    if (!window.confirm(`ยืนยันลบรายการสต๊อกนี้?\nเหตุผล: ${m.reason}\nRef: ${m.ref_no || "-"}`)) {
      return;
    }

    try {
      setDeletingId(m.id);
      const url = apiUrl(`/inventory_movements/${m.id}`);
      const resp = await fetch(url, { method: "DELETE" });

      const ct = resp.headers.get("content-type") || "";
      const isJson = ct.includes("application/json");
      const payload = isJson ? await resp.json() : await resp.text();

      if (!resp.ok || (isJson && payload?.ok === false)) {
        const msg = isJson ? (payload?.error ?? resp.statusText) : String(payload);
        throw new Error(msg || "delete_failed");
      }

      // ลบออกจากรายการใน modal
      setMovItems((old) => old.filter((it) => it.id !== m.id));
      setToast({ show: true, message: "ลบรายการสต๊อกสำเร็จ", type: "success" });

      // รีเฟรชคงเหลือของสินค้าในรายการหลัก
      refresh();
    } catch (err: any) {
      const msg = err?.message ?? "ลบรายการสต๊อกไม่สำเร็จ";
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setDeletingId(null);
    }
  };

  /** ---------- UI ---------- */
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      {/* Toast มุมขวาบน */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toast.show && (
          <div className={`alert alert-${toast.type ?? "success"} shadow mb-0 rounded-3 py-2 px-3`}>
            {toast.message}
          </div>
        )}
      </div>

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-9">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                {/* Header + actions */}
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                  <h4 className="mb-0 text-primary">จัดการสต๊อกสินค้า</h4>
                  <div className="d-flex flex-wrap gap-2">
                    <Link
                      to="/ProductsAddPage"
                      className="btn btn-success btn-sm d-inline-flex align-items-center gap-2"
                    >
                      <span className="fw-bold">+</span> เพิ่มสินค้าใหม่
                    </Link>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={refresh}>
                      รีเฟรช
                    </button>
                  </div>
                </div>

                {/* ค้นหา */}
                <div className="input-group mb-3">
                  <input
                    className="form-control"
                    placeholder="ค้นหาโค้ด/ชื่อ/หน่วย (กด Enter เพื่อค้นหา server)"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                  />
                  <button className="btn btn-primary" type="button" onClick={doSearch}>
                    ค้นหา
                  </button>
                </div>

                {/* สถานะโหลด/ผิดพลาด */}
                {loading && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span>กำลังโหลดรายการสินค้า…</span>
                  </div>
                )}
                {errorMsg && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    โหลดสินค้าไม่สำเร็จ: {errorMsg}
                  </div>
                )}

                {/* ตารางสินค้า */}
                <div className="border rounded-3 p-0 bg-white">
                  <div className="table-responsive" style={{ maxHeight: 520 }}>
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: 76 }}>รูป</th>
                          <th style={{ width: 160 }}>โค้ด</th>
                          <th>ชื่อสินค้า</th>
                          <th style={{ width: 140 }} className="text-end">
                            ราคาปลีก
                          </th>
                          <th style={{ width: 140 }} className="text-end">
                            ราคาส่ง
                          </th>
                          <th style={{ width: 120 }} className="text-end">
                            คงเหลือ
                          </th>
                          <th style={{ width: 180 }} className="text-end">
                            จัดการ
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.length === 0 && !loading && (
                          <tr>
                            <td colSpan={7} className="text-center text-muted py-4">
                              ไม่พบรายการ
                            </td>
                          </tr>
                        )}

                        {filtered.map((p) => (
                          <tr key={p.id} className="user-select-none">
                            <td>
                              {(() => {
                                const thumbSrc = p.thumbUrl || p.imageUrl || null;
                                const fullSrc = p.imageUrl || thumbSrc || null;

                                return thumbSrc ? (
                                  <img
                                    src={thumbSrc}
                                    alt={p.name}
                                    width={56}
                                    height={56}
                                    loading="lazy"
                                    decoding="async"
                                    style={{ objectFit: "cover", borderRadius: 8, background: "#f5f5f5" }}
                                    onMouseEnter={() => setPreviewImage(fullSrc)}
                                    onMouseLeave={() => setPreviewImage(null)}
                                    onError={(e) => {
                                      (e.currentTarget as HTMLImageElement).style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <div className="bg-light border rounded" style={{ width: 56, height: 56 }} />
                                );
                              })()}
                            </td>
                            <td className="fw-semibold">{p.code}</td>
                            <td>{p.name}</td>
                            <td className="text-end">
                              {p.price.toLocaleString("th-TH", { style: "currency", currency: "THB" })}
                            </td>
                            <td className="text-end">
                              {p.wholesalePrice.toLocaleString("th-TH", { style: "currency", currency: "THB" })}
                            </td>
                            <td className="text-end">{p.stockOnHand}</td>
                            <td className="text-end">
                              <div className="btn-group btn-group-sm">
                                {/* เอาปุ่มอัปโหลดรูปออก เหลือแค่ประวัติ */}
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  title="ประวัติการเคลื่อนไหว"
                                  onClick={() => openMovements(p.code)}
                                >
                                  ประวัติ / ลบสต๊อก
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="small text-muted">
                    แสดง {items.length} รายการ / ทั้งหมด {total} รายการ
                  </div>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={page <= 1 || loading}
                      onClick={() => {
                        const newPage = page - 1;
                        setPage(newPage);
                        fetchProducts(q || undefined, newPage);
                      }}
                    >
                      ← ก่อนหน้า
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={page * limit >= total || loading}
                      onClick={() => {
                        const newPage = page + 1;
                        setPage(newPage);
                        fetchProducts(q || undefined, newPage);
                      }}
                    >
                      ถัดไป →
                    </button>
                  </div>
                </div>

                <div className="text-center small text-muted mt-3">
                  * หน้าไว้ดู/แก้ประวัติสต๊อก ลบ movement ผิดได้
                </div>
                <a href="/" className="btn btn-link text-decoration-none flex-fill text-center">
                  ← กลับหน้าเมนู
                </a>
              </div>
            </div>

            {/* Hover Preview กลางจอ */}
            {previewImage && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ backgroundColor: "rgba(0,0,0,0.7)", zIndex: 2000, pointerEvents: "none" }}
              >
                <img
                  src={previewImage}
                  alt="preview"
                  style={{
                    maxWidth: "90%",
                    maxHeight: "90%",
                    borderRadius: 12,
                    boxShadow: "0 0 20px rgba(0,0,0,0.5)",
                    background: "#fff",
                    transition: "opacity .15s ease",
                    opacity: 1,
                  }}
                />
              </div>
            )}

            {/* ---------- Modal: Movement History + Delete ---------- */}
            {showMovModal && (
              <div
                className="modal fade show"
                role="dialog"
                style={{ display: "block", backgroundColor: "rgba(0,0,0,.5)" }}
                aria-modal="true"
                aria-labelledby="movModalLabel"
                onClick={(e) => {
                  if (e.target === e.currentTarget) setShowMovModal(false);
                }}
              >
                <div className="modal-dialog modal-lg modal-dialog-scrollable">
                  <div className="modal-content rounded-4 shadow">
                    <div className="modal-header">
                      <h5 className="modal-title" id="movModalLabel">
                        ประวัติความเคลื่อนไหว: {movCode}
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => setShowMovModal(false)}
                      />
                    </div>

                    <div className="modal-body">
                      {movLoading && (
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div className="spinner-border spinner-border-sm" role="status" />
                          <span>กำลังโหลดประวัติ…</span>
                        </div>
                      )}
                      {movError && (
                        <div className="alert alert-warning rounded-3" role="alert">
                          โหลดประวัติไม่สำเร็จ: {movError}
                        </div>
                      )}

                      <div className="table-responsive" style={{ maxHeight: 420 }}>
                        <table className="table table-sm table-hover align-middle mb-0">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th style={{ width: 140 }}>วันที่</th>
                              <th style={{ width: 100 }}>เหตุผล</th>
                              <th className="text-end" style={{ width: 100 }}>
                                รับเข้า (+)
                              </th>
                              <th className="text-end" style={{ width: 100 }}>
                                จ่ายออก (-)
                              </th>
                              <th className="text-end" style={{ width: 120 }}>
                                ต้นทุน/หน่วย
                              </th>
                              <th>อ้างอิง</th>
                              <th style={{ width: 110 }} className="text-end">
                                ลบสต๊อก
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {movItems.length === 0 && !movLoading && (
                              <tr>
                                <td colSpan={7} className="text-center text-muted py-3">
                                  ไม่มีข้อมูล
                                </td>
                              </tr>
                            )}
                            {movItems.map((m) => (
                              <tr
                                key={m.id}
                                style={
                                  m.status === "sold_out"
                                    ? { backgroundColor: "rgba(25, 135, 84, 0.12)" }
                                    : undefined
                                }
                                title={m.status === "sold_out" ? "ขายหมด (sold out)" : undefined}
                              >
                                <td>{new Date(m.created_at).toLocaleString("th-TH")}</td>
                                <td>
                                  {m.reason === "PURCHASE"
                                    ? "รับเข้า"
                                    : m.reason === "SALE"
                                    ? "ขายออก"
                                    : "ปรับสต็อก"}
                                  {m.status === "sold_out" && (
                                    <span
                                      className="ms-2 badge rounded-pill text-bg-success"
                                      style={{ opacity: 0.85 }}
                                    >
                                      sold out
                                    </span>
                                  )}
                                </td>
                                <td className="text-end">{m.qty > 0 ? m.qty : "-"}</td>
                                <td className="text-end">{m.qty_sell > 0 ? m.qty_sell : "-"}</td>
                                <td className="text-end">
                                  {m.unit_cost.toLocaleString("th-TH", {
                                    style: "currency",
                                    currency: "THB",
                                  })}
                                </td>
                                <td>
                                  {m.ref_table || "-"} {m.ref_no ? `#${m.ref_no}` : ""}
                                </td>
                                <td className="text-end">
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    disabled={deletingId === m.id}
                                    onClick={() => deleteMovement(m)}
                                  >
                                    {deletingId === m.id ? "กำลังลบ…" : "ลบ"}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="modal-footer d-flex justify-content-between">
                      <div className="btn-group">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={movOffset === 0 || movLoading}
                          onClick={prevMovPage}
                        >
                          ← ก่อนหน้า
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={movItems.length < movLimit || movLoading}
                          onClick={nextMovPage}
                        >
                          ถัดไป →
                        </button>
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowMovModal(false)}
                      >
                        ปิด
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* ---------- /Modal: Movement History ---------- */}
          </div>
        </div>
      </div>
    </div>
  );
}
